import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, TrendingUp, Briefcase, DollarSign, BarChart3, ArrowRight, ChevronUp, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

function AnimatedCounter({ value, prefix = "", suffix = "" }: { value: number; prefix?: string; suffix?: string }) {
  const [display, setDisplay] = useState(0);
  const prevValue = useRef(0);

  useEffect(() => {
    const start = prevValue.current;
    const end = value;
    const duration = 800;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + (end - start) * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
    prevValue.current = end;
  }, [value]);

  return <span>{prefix}{display.toLocaleString()}{suffix}</span>;
}

export function RoiCalculator() {
  const [avgJobValue, setAvgJobValue] = useState("");
  const [currentJobs, setCurrentJobs] = useState("");
  const [serviceArea, setServiceArea] = useState("");
  const [growthPercent, setGrowthPercent] = useState(30);
  const [showResults, setShowResults] = useState(false);

  const jobValue = parseFloat(avgJobValue) || 0;
  const jobs = parseFloat(currentJobs) || 15; // default estimate
  const extraJobs = Math.round(jobs * (growthPercent / 100));
  const extraRevenue = extraJobs * jobValue;
  const yearlyExtra = extraRevenue * 12;
  // No fixed investment shown — pricing varies per client

  const canCalculate = jobValue > 0;

  const handleCalculate = () => {
    if (!canCalculate) return;
    setShowResults(true);
  };

  return (
    <section className="section-padding bg-background relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-25" />
      <div className="container-wide relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <div className="section-badge mb-4">
            <span className="text-xs">ROI Calculator</span>
          </div>
          <h2 className="heading-section text-foreground mb-3">
            Estimate Your <span className="text-gradient">Ad Campaign</span> ROI
          </h2>
          <p className="text-muted-foreground text-base max-w-xl mx-auto">
            See how much extra revenue targeted Google & Facebook Ads could generate for your trade business.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-card rounded-xl border border-border/60 shadow-card p-6 sm:p-8">
            {/* Inputs */}
            <div className="space-y-5">
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <label className="block text-foreground text-sm font-semibold">
                    Average Job Value ($)
                  </label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground text-xs cursor-help rounded-full border border-border w-4 h-4 inline-flex items-center justify-center">?</span>
                      </TooltipTrigger>
                      <TooltipContent><p>What you normally charge per job — e.g. $1,500 for a bathroom reno.</p></TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-muted-foreground text-xs mb-2">What do you typically charge per job?</p>
                <input
                  type="number"
                  value={avgJobValue}
                  onChange={(e) => { setAvgJobValue(e.target.value); setShowResults(false); }}
                  placeholder="e.g. 1500"
                  className="w-full h-11 px-4 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>

              <div>
                <label className="block text-foreground text-sm font-semibold mb-1.5">
                  Current Jobs per Month <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <p className="text-muted-foreground text-xs mb-2">How many jobs are you doing now? Leave blank if unsure — we'll estimate.</p>
                <input
                  type="number"
                  value={currentJobs}
                  onChange={(e) => { setCurrentJobs(e.target.value); setShowResults(false); }}
                  placeholder="e.g. 15 (we'll estimate if blank)"
                  className="w-full h-11 px-4 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>

              <div>
                <label className="block text-foreground text-sm font-semibold mb-1.5">
                  Service Area / City <span className="text-muted-foreground font-normal">(optional)</span>
                </label>
                <p className="text-muted-foreground text-xs mb-2">Where do you work? Helps us tailor your estimate.</p>
                <input
                  type="text"
                  value={serviceArea}
                  onChange={(e) => setServiceArea(e.target.value)}
                  placeholder="e.g. Sydney, Melbourne, Brisbane"
                  className="w-full h-11 px-4 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>

              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <label className="block text-foreground text-sm font-semibold">
                    Growth Goal: <span className="text-primary font-bold">{growthPercent}% more jobs</span>
                  </label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground text-xs cursor-help rounded-full border border-border w-4 h-4 inline-flex items-center justify-center">?</span>
                      </TooltipTrigger>
                      <TooltipContent><p>Based on the range of results our ad campaigns have delivered. 10% is conservative, 100% is aggressive — actual results depend on your trade, ad budget, location, and competition.</p></TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-muted-foreground text-xs mb-3">How aggressive do you want to grow?</p>
                <div className="px-1">
                  <Slider
                    value={[growthPercent]}
                    onValueChange={(val) => { setGrowthPercent(val[0]); setShowResults(false); }}
                    min={10}
                    max={100}
                    step={5}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
                    <span>Conservative (10%)</span>
                    <span>Aggressive (100%)</span>
                  </div>
                </div>
              </div>

              <Button onClick={handleCalculate} className="w-full" size="lg" disabled={!canCalculate}>
                <Calculator className="w-4 h-4 mr-2" />
                Show Me My Ad Campaign ROI
              </Button>
            </div>

            {/* Results */}
            <AnimatePresence>
              {showResults && canCalculate && (
                <motion.div
                  initial={{ opacity: 0, y: 20, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: 10, height: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="mt-8 space-y-6 overflow-hidden"
                >
                  {/* Big numbers */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.2, duration: 0.4 }}
                      className="bg-primary/10 border border-primary/20 rounded-xl p-6 text-center"
                    >
                      <Briefcase className="w-8 h-8 text-primary mx-auto mb-2" />
                      <p className="text-4xl sm:text-5xl font-extrabold text-primary">
                        <AnimatedCounter value={extraJobs} prefix="+" />
                      </p>
                       <p className="text-foreground font-semibold text-sm mt-2">Extra Leads per Month</p>
                       <p className="text-muted-foreground text-xs mt-1">From your ad campaigns</p>
                    </motion.div>

                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.35, duration: 0.4 }}
                      className="bg-primary/10 border border-primary/20 rounded-xl p-6 text-center"
                    >
                      <DollarSign className="w-8 h-8 text-primary mx-auto mb-2" />
                      <p className="text-4xl sm:text-5xl font-extrabold text-primary">
                        <AnimatedCounter value={extraRevenue} prefix="$" />
                      </p>
                       <p className="text-foreground font-semibold text-sm mt-2">Extra Revenue per Month</p>
                       <p className="text-muted-foreground text-xs mt-1">Estimated from ad-generated leads</p>
                    </motion.div>
                  </div>

                  {/* Yearly projection */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/15 rounded-xl p-5 text-center"
                  >
                    <p className="text-muted-foreground text-sm mb-1">That could be an estimated</p>
                    <p className="text-3xl sm:text-4xl font-extrabold text-foreground">
                      <AnimatedCounter value={yearlyExtra} prefix="$" /><span className="text-primary">/year</span>
                    </p>
                    <p className="text-muted-foreground text-sm mt-1">in estimated additional revenue for your business</p>
                  </motion.div>

                  {/* Comparison: Without vs With */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <h3 className="text-foreground font-bold text-base text-center mb-4">Your Business: Now vs With FullScale</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Without */}
                      <div className="bg-muted/50 border border-border rounded-xl p-4 text-center">
                        <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-3">Without Us</p>
                        <div className="space-y-3">
                          <div>
                            <p className="text-2xl font-bold text-foreground">{jobs}</p>
                            <p className="text-xs text-muted-foreground">Jobs / month</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-foreground">${(jobs * jobValue).toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">Revenue / month</p>
                          </div>
                          {/* Visual bar */}
                          <div className="h-2 bg-border rounded-full overflow-hidden">
                            <div className="h-full bg-muted-foreground/40 rounded-full" style={{ width: "60%" }} />
                          </div>
                        </div>
                      </div>

                      {/* With FullScale */}
                      <div className="bg-primary/10 border-2 border-primary/30 rounded-xl p-4 text-center relative">
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[10px] font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> RECOMMENDED
                        </div>
                        <p className="text-xs text-primary font-semibold uppercase tracking-wider mb-3">With FullScale</p>
                        <div className="space-y-3">
                          <div>
                            <p className="text-2xl font-bold text-primary">{jobs + extraJobs}</p>
                            <p className="text-xs text-muted-foreground">Jobs / month</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-primary">${((jobs + extraJobs) * jobValue).toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">Revenue / month</p>
                          </div>
                          {/* Visual bar */}
                          <div className="h-2 bg-primary/20 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: "60%" }}
                              animate={{ width: "100%" }}
                              transition={{ delay: 0.8, duration: 0.6 }}
                              className="h-full bg-primary rounded-full"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Disclaimer */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="text-center"
                  >
                    <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold text-foreground">
                        These are <span className="text-primary">estimates</span> based on typical client results — your actual results may vary
                      </span>
                    </div>
                  </motion.div>

                  {/* Social proof */}
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9 }}
                    className="text-muted-foreground text-xs text-center leading-relaxed"
                  >
                    ⭐ Clients running Google & Facebook Ads with FullScale have seen <strong className="text-foreground">20–50% more leads</strong> in 3–6 months. Individual results vary.
                  </motion.p>

                  {/* CTA */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1 }}
                    className="text-center pt-2"
                  >
                    <Button size="xl" className="group" asChild>
                      <a href="#contact-section">
                        Book a Free Strategy Call — See What's Possible
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </a>
                    </Button>
                    
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
